/**
 * Client emitter for refresh-failure auto-logout.
 *
 * Fire-and-forget by contract: this runs inside `useAuth.refreshToken()`'s catch, immediately
 * before `logout()`. Analytics is strictly subordinate to auth — a dropped diagnostic is always
 * preferable to a delayed or altered logout — so the whole body is guarded and nothing is awaited.
 *
 * WHY THIS BYPASSES `analyticsService.track()` (and does not use the existing
 * `AnalyticsEvents.USER_AUTO_LOGOUT` policy entry): three independent mechanisms would each drop
 * or corrupt this event.
 *
 *  1. `AnalyticsService.canSend` requires `runtimePolicy.isAuthenticated()`, backed by
 *     `getAuthenticatedContactId` (`plugins/analytics.client.ts:64`). Auto-logout is exactly the
 *     moment that predicate flips false.
 *  2. `AnalyticsService.enrichProperties` filters caller properties through
 *     `RESERVED_CONTEXT_KEYS` (`services/analytics.service.ts:56-66`), which includes
 *     `locationId`, `contactId`, and `userId` — a caller cannot pass a tenant id through. The
 *     existing `analytics-context-resolver-error` event works around this with a renamed
 *     `diagnosticLocationId` and consequently lands `locationId: 'NA'`.
 *  3. `providers.backendAnalytics.enabled` is `false` in `createAnalyticsNuxtConfig`
 *     (`config/analytics-nuxt-config.ts:49`) and is overridden by no app, so the provider is
 *     never registered (`plugins/analytics.client.ts:92`). Flipping that flag would also switch
 *     on backend delivery for the unrelated `analytics-context-resolver-error`.
 *
 * Transport is delegated to `backendAnalyticsService`, which already targets
 * `COMMUNITIES_SERVER_URL` + `/analytics/track-event`, already sends the `channel`/`source`
 * headers the endpoint expects, and is already exempt from bearer-token attachment via
 * `BaseService.shouldSkipToken` — required here, because the token is dead by definition.
 */

import { useLogger } from '@common/core-layer/composables/useLogger'

import { buildAutoLogoutTrackEventPayload } from '../tracking/auto-logout-envelope'
import { backendAnalyticsService } from '../services/backend-analytics.service'
import type { AutoLogoutEnvelopeInput, AutoLogoutOrigin } from '../tracking/auto-logout-envelope'

/**
 * Client-side callers. `origin` defaults to `client` (mid-session logout); the boot plugin passes
 * `client-boot` so operators can separate boot-time session deaths from 401-path ones.
 */
export type ClientAutoLogoutInput = Omit<AutoLogoutEnvelopeInput, 'origin'> & {
  origin?: Extract<AutoLogoutOrigin, 'client' | 'client-boot'>
}

/**
 * Emits one `client-portal-user-auto-logout` event for a client-side refresh failure.
 *
 * Returns `void` synchronously. Never throws: a transport failure is logged and swallowed so the
 * caller's `logout()` runs unchanged.
 */
export function trackRefreshFailureAutoLogout(input: ClientAutoLogoutInput): void {
  const { logger } = useLogger()

  try {
    const payload = buildAutoLogoutTrackEventPayload({ ...input, origin: input.origin ?? 'client' })

    // Fire-and-forget, mirroring `clientportal-analytics.service.ts` trackPageHit: the outer
    // function stays synchronous while the awaited call is isolated inside a void IIFE, so a
    // rejected transport can never surface as an unhandled rejection after we return.
    void (async () => {
      try {
        await backendAnalyticsService.trackEvent(payload)
      } catch (error: unknown) {
        logger.warn('[Analytics] Auto-logout event delivery failed', {
          labels: { context: 'analytics.auto-logout' },
          error: error instanceof Error ? error : new Error(String(error)),
        })
      }
    })()
  } catch (error: unknown) {
    logger.warn('[Analytics] Auto-logout event could not be emitted', {
      labels: { context: 'analytics.auto-logout' },
      error: error instanceof Error ? error : new Error(String(error)),
    })
  }
}
